import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  collection, doc, getDoc, getDocs, query, where, deleteDoc, 
  writeBatch, setDoc, orderBy, startAfter, limit, updateDoc, deleteField
} from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faEdit, faTrash, faFilePdf, 
  faFileImage, faFileAlt, faSpinner, faSearch, faEye, faExchangeAlt, faLock
} from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import AddAudioEquipmentModal from '../common/AddAudioEquipmentModal';
import AddBacklineEquipmentModal from '../common/AddBacklineEquipmentModal';
import AddLightingEquipmentModal from '../common/AddLightingEquipmentModal';
import AddVisualEquipmentModal from '../common/AddVisualEquipmentModal';
import AddPyroEquipmentModal from '../common/AddPyroEquipmentModal';
import AddStagingEquipmentModal from '../common/AddStagingEquipmentModal';
import AddMiscellaneousEquipmentModal from '../common/AddMiscellaneousEquipmentModal';
import FileViewerModal from '../common/FileViewerModal';
import ConfirmModal from '../common/ConfirmModal';
import AssignInventoryModal from '../common/AssignInventoryModal';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './Inventory.module.css';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { sendInventoryUpdateNotification } from '../../utils/NotificationService';

// Permission Denied Component
const PermissionDenied = () => (
  <div className={styles.permissionDenied}>
    <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
    <h2>Access Restricted</h2>
    <p>You don't have permission to view this page. This section is only available to Vendor and Staff accounts.</p>
  </div>
);

const Inventory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useContext(UserContext);
  const [fullSearchResults, setFullSearchResults] = useState([]);
  const [currentResultsPage, setCurrentResultsPage] = useState(1);
  const [hasAccess, setHasAccess] = useState(false);

   // Check if user has access based on userType
   useEffect(() => {
    if (userData) {
      setHasAccess(userData.userType === 'Vendor' || userData.userType === 'Staff');
    }
  }, [userData]);

  const [searchCache, setSearchCache] = useState({
    audioEquipment: {},
    backlineEquipment: {},
    lightingEquipment: {},
    visualEquipment: {},
    pyroEquipment: {},
    stagingEquipment: {},
    miscellaneousEquipment: {}
  });

  // Inventory data from the new structure
  const [inventoryGroups, setInventoryGroups] = useState({
    audioEquipment: [],
    backlineEquipment: [],
    lightingEquipment: [],
    visualEquipment: [],
    pyroEquipment: [],
    stagingEquipment: [],
    miscellaneousEquipment: []
  });
  
  // Pagination and search state
  const [hasMoreItems, setHasMoreItems] = useState({
    audioEquipment: true,
    backlineEquipment: true,
    lightingEquipment: true,
    visualEquipment: true,
    pyroEquipment: true,
    stagingEquipment: true,
    miscellaneousEquipment: true
  });
  const [lastDocSnapshot, setLastDocSnapshot] = useState({
    audioEquipment: null,
    backlineEquipment: null,
    lightingEquipment: null,
    visualEquipment: null,
    pyroEquipment: null,
    stagingEquipment: null,
    miscellaneousEquipment: null
  });
  
  // Search-related state
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);
  const [lastSearchDocSnapshot, setLastSearchDocSnapshot] = useState(null);
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [tours, setTours] = useState([]); // For RightMenu
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Modal states
  const [showAddAudioModal, setShowAddAudioModal] = useState(false);
  const [showAddBacklineModal, setShowAddBacklineModal] = useState(false);
  const [showAddLightingModal, setShowAddLightingModal] = useState(false);
  const [showAddVisualModal, setShowAddVisualModal] = useState(false);
  const [showAddPyroModal, setShowAddPyroModal] = useState(false);
  const [showAddStagingModal, setShowAddStagingModal] = useState(false);
  const [showAddMiscellaneousModal, setShowAddMiscellaneousModal] = useState(false);
  const [showAssignInventoryModal, setShowAssignInventoryModal] = useState(false);
  
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [fileViewerUrl, setFileViewerUrl] = useState('');
  const [fileViewerType, setFileViewerType] = useState('');
  const [fileViewerName, setFileViewerName] = useState('');
  
  const [editItem, setEditItem] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const [staffPermissions, setStaffPermissions] = useState({
    canAddItems: true,
    canEditItems: true,
    canDeleteItems: true,
    canChangeStatus: true
  });


  // Check for screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (userData) {
      // Check if the user is staff and set permissions
      setStaffPermissions(getStaffPermissions());
    }
  }, [userData]);

  // Constants
  const ITEMS_PER_PAGE = 10; // Change to 10 per page as requested

  // Equipment categories mapping for tabs
  const equipmentCategories = [
    { id: 'audioEquipment', label: 'Audio Equipment' },
    { id: 'backlineEquipment', label: 'Backline Equipment' },
    { id: 'lightingEquipment', label: 'Lighting Equipment' },
    { id: 'visualEquipment', label: 'Visual Equipment' },
    { id: 'pyroEquipment', label: 'Pyro Equipment' },
    { id: 'stagingEquipment', label: 'Staging Equipment' },
    { id: 'miscellaneousEquipment', label: 'Miscellaneous Equipment' }
  ];
  
  // Get category display name
  const getCategoryDisplayName = (categoryId) => {
    const categories = {
      'audioEquipment': 'Audio Equipment',
      'backlineEquipment': 'Backline Equipment',
      'lightingEquipment': 'Lighting Equipment',
      'visualEquipment': 'Visual Equipment',
      'pyroEquipment': 'Pyro Equipment',
      'stagingEquipment': 'Staging Equipment',
      'miscellaneousEquipment': 'Miscellaneous Equipment'
    };
    return categories[categoryId] || 'Equipment';
  };

  // Check URL for edit parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const editParam = searchParams.get('edit');
    
    if (editParam) {
      const [category, itemId] = editParam.split('_');
      
      if (category && itemId) {
        handleEditItemById(category, itemId);
      }
    }
  }, [location.search, inventoryGroups]);
  
  // Function to handle edit by ID (from URL or direct call)
  const handleEditItemById = async (category, itemId) => {
    // First check if item is in inventoryGroups
    const groupItem = inventoryGroups[category]?.find(item => item.id === itemId || item.groupId === itemId);
    
    if (groupItem) {
      handleEditItem(groupItem, category);
      return;
    }
    
    // If not found in memory, try to fetch from Firestore
    try {
      const db = getFirestoreInstance();
      
      // Try to get from inventoryGroups collection
      const groupRef = doc(db, 'users', userData.userId, 'inventoryGroups', itemId);
      const groupDoc = await getDoc(groupRef);
      
      if (groupDoc.exists()) {
        const fetchedItem = {
          id: groupDoc.id,
          ...groupDoc.data()
        };
        handleEditItem(fetchedItem, category);
      } else {
        console.error(`Item not found: ${category}_${itemId}`);
      }
    } catch (error) {
      console.error('Error fetching item for edit:', error);
    }
  };

  // Fetch inventory data
  useEffect(() => {
    if (!userData || !userData.userId) {
      setIsLoading(false);
      return;
    }

    const fetchInventory = async () => {
      try {
        setIsLoading(true);
        
        // Reset states when starting a new fetch
        if (searchTerm.trim() === '') {
          setInventoryGroups({
            audioEquipment: [],
            backlineEquipment: [],
            lightingEquipment: [],
            visualEquipment: [],
            pyroEquipment: [],
            stagingEquipment: [],
            miscellaneousEquipment: []
          });
          
          setLastDocSnapshot({
            audioEquipment: null,
            backlineEquipment: null,
            lightingEquipment: null,
            visualEquipment: null,
            pyroEquipment: null,
            stagingEquipment: null,
            miscellaneousEquipment: null
          });
          
          setHasMoreItems({
            audioEquipment: true,
            backlineEquipment: true,
            lightingEquipment: true,
            visualEquipment: true,
            pyroEquipment: true,
            stagingEquipment: true,
            miscellaneousEquipment: true
          });
        }
        
        // Only fetch current tab data initially
        const currentCategory = equipmentCategories[activeTab].id;
        await fetchInventoryByCategory(currentCategory);
        
      } catch (error) {
        console.error('Error fetching inventory:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInventory();
  }, [userData, activeTab]);
  
  // Function to fetch inventory data for a specific category
  const fetchInventoryByCategory = async (category, isLoadMore = false) => {
    if (!userData?.userId) return;
    
    try {
      const db = getFirestoreInstance();
      
      // Create base query for the category
      let inventoryQuery;
      
      if (isLoadMore && lastDocSnapshot[category]) {
        // Load more - start after last document
        inventoryQuery = query(
          collection(db, 'users', userData.userId, 'inventoryGroups'),
          where('inventoryType', '==', getCategoryDisplayName(category)),
          orderBy('updatedAt', 'desc'),
          startAfter(lastDocSnapshot[category]),
          limit(ITEMS_PER_PAGE)
        );
      } else {
        // Initial load
        inventoryQuery = query(
          collection(db, 'users', userData.userId, 'inventoryGroups'),
          where('inventoryType', '==', getCategoryDisplayName(category)),
          orderBy('updatedAt', 'desc'),
          limit(ITEMS_PER_PAGE)
        );
      }
      
      const querySnapshot = await getDocs(inventoryQuery);
      
      // Check if we have more items
      setHasMoreItems(prev => ({
        ...prev,
        [category]: querySnapshot.docs.length === ITEMS_PER_PAGE
      }));
      
      // Save the last document for pagination
      if (querySnapshot.docs.length > 0) {
        setLastDocSnapshot(prev => ({
          ...prev,
          [category]: querySnapshot.docs[querySnapshot.docs.length - 1]
        }));
        
        // Extract items
        const fetchedItems = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Update state
        setInventoryGroups(prev => ({
          ...prev,
          [category]: isLoadMore ? [...prev[category], ...fetchedItems] : fetchedItems
        }));
      } else {
        // No more items
        setHasMoreItems(prev => ({
          ...prev,
          [category]: false
        }));
      }
    } catch (error) {
      console.error(`Error fetching ${category} inventory:`, error);
    }
  };
  
  const getStaffPermissions = () => {
    // If not staff, assume full permissions
    if (!userData || userData.userType !== 'Staff') {
      return {
        canAddItems: true,
        canEditItems: true,
        canDeleteItems: true,
        canChangeStatus: true
      };
    }
    
    // Check for organization data in userData
    const vendorOrganization = userData.organizations?.find(org => 
      org.organizationId === userData.currentOrganizationId
    );
    
    // Return the permissions from organization data or default to no permissions
    return vendorOrganization?.permissions || {
      canAddItems: false,
      canEditItems: false,
      canDeleteItems: false, 
      canChangeStatus: false
    };
  };
 
  // Helper function to determine if the action is allowed
const isActionAllowed = (action) => {
  if (!userData || userData.userType !== 'Staff') {
    return true; // Non-staff users have full permissions
  }
  
  switch (action) {
    case 'add':
      return staffPermissions.canAddItems;
    case 'edit':
      return staffPermissions.canEditItems;
    case 'delete':
      return staffPermissions.canDeleteItems;
    case 'status':
      return staffPermissions.canChangeStatus;
    default:
      return false;
  }
};


const handleAddItem = (category) => {
  // Reset editItem to null to clear any previous data
  setEditItem(null);
  
  // Check if staff has permission to add
  if (!isActionAllowed('add')) {
    alert('You do not have permission to add inventory items.');
    return;
  }
  
  // Show the appropriate modal based on category
  switch (category) {
    case 'audioEquipment':
      setShowAddAudioModal(true);
      break;
    case 'backlineEquipment':
      setShowAddBacklineModal(true);
      break;
    case 'lightingEquipment':
      setShowAddLightingModal(true);
      break;
    case 'visualEquipment':
      setShowAddVisualModal(true);
      break;
    case 'pyroEquipment':
      setShowAddPyroModal(true);
      break;
    case 'stagingEquipment':
      setShowAddStagingModal(true);
      break;
    case 'miscellaneousEquipment':
      setShowAddMiscellaneousModal(true);
      break;
  }
};


  
  const handleSearch = async () => {
    if (!userData?.userId || !searchTerm.trim()) {
      if (isSearching) {
        setIsSearching(false);
        setSearchResults([]);
      }
      return;
    }
    
    setIsSearching(true);
    setIsLoading(true);
    
    try {
      const currentCategory = equipmentCategories[activeTab].id;
      const searchTermLower = searchTerm.trim().toLowerCase();
      
      // Check if we have this search cached for this category
      if (searchCache[currentCategory] && searchCache[currentCategory][searchTermLower]) {
        console.log('Using cached search results for:', searchTermLower, 'in category:', currentCategory);
        const cachedResults = searchCache[currentCategory][searchTermLower];
        
        setFullSearchResults(cachedResults);
        setCurrentResultsPage(1);
        setSearchResults(cachedResults.slice(0, ITEMS_PER_PAGE));
        setHasMoreSearchResults(cachedResults.length > ITEMS_PER_PAGE);
        setIsLoading(false);
        return;
      }
      
      // If not in cache, proceed with the search
      const db = getFirestoreInstance();
      
      // Split search into words
      const words = searchTermLower.split(/\s+/);
      
      // Try multiple approaches to find the best results
      let allResults = [];
      let querySnapshot;
      
      // APPROACH 1: Try exact match first (if search has multiple words)
      if (words.length > 1) {
        // Search for the exact multi-word term (with space)
        querySnapshot = await getDocs(query(
          collection(db, 'users', userData.userId, 'inventoryGroups'),
          where('keywords', 'array-contains', searchTermLower),
          orderBy('updatedAt', 'desc'),
          limit(50)
        ));
        
        allResults = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          matchQuality: 3 // Highest quality - exact phrase match
        }));
      }
      
      // APPROACH 2: Try the term without spaces
      if (words.length > 1 && allResults.length < 10) {
        const noSpaceTerm = searchTermLower.replace(/\s+/g, '');
        querySnapshot = await getDocs(query(
          collection(db, 'users', userData.userId, 'inventoryGroups'),
          where('keywords', 'array-contains', noSpaceTerm),
          orderBy('updatedAt', 'desc'),
          limit(50)
        ));
        
        const noSpaceResults = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          matchQuality: 2 // Medium quality - no-space match
        }));
        
        // Combine results, avoiding duplicates
        const existingIds = new Set(allResults.map(r => r.id));
        allResults = [...allResults, ...noSpaceResults.filter(r => !existingIds.has(r.id))];
      }
      
      // APPROACH 3: Use prefix/partial match for single term searches
      const firstWord = words[0];
      
      // Different strategy based on search term length
      if (firstWord.length <= 3) {
        // For very short terms (2-3 chars), get all documents and filter client-side
        // This is more efficient than making many individual queries for short prefixes
        querySnapshot = await getDocs(query(
          collection(db, 'users', userData.userId, 'inventoryGroups'),
          where('inventoryType', '==', getCategoryDisplayName(equipmentCategories[activeTab].id)),
          orderBy('updatedAt', 'desc'),
          limit(100) // Increased limit for better results with short terms
        ));
        
        const partialMatches = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            matchQuality: 1
          }))
          .filter(item => {
            // Check if keywords array contains any keyword that includes the search term
            return item.keywords?.some(keyword => 
              keyword.includes(firstWord)
            );
          });
        
        // Combine results, avoiding duplicates
        const existingIds = new Set(allResults.map(r => r.id));
        allResults = [...allResults, ...partialMatches.filter(r => !existingIds.has(r.id))];
      } else {
        // For longer terms, try exact keyword match first
        querySnapshot = await getDocs(query(
          collection(db, 'users', userData.userId, 'inventoryGroups'),
          where('keywords', 'array-contains', firstWord),
          orderBy('updatedAt', 'desc'),
          limit(50)
        ));
        
        const firstWordResults = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          matchQuality: 1
        }));
        
        // Combine results, avoiding duplicates
        const existingIds = new Set(allResults.map(r => r.id));
        allResults = [...allResults, ...firstWordResults.filter(r => !existingIds.has(r.id))];
        
        // If we still need more results, try partial matches
        if (allResults.length < 10) {
          // Get all documents for the current category and filter client-side for partial matches
          querySnapshot = await getDocs(query(
            collection(db, 'users', userData.userId, 'inventoryGroups'),
            where('inventoryType', '==', getCategoryDisplayName(equipmentCategories[activeTab].id)),
            orderBy('updatedAt', 'desc'),
            limit(50)
          ));
          
          const partialMatches = querySnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data(),
              matchQuality: 0 // Lowest quality - partial match
            }))
            .filter(item => {
              // Check if not already in results and has a keyword containing the search term
              return !existingIds.has(item.id) && 
                    item.keywords?.some(keyword => keyword.includes(firstWord));
            });
          
          allResults = [...allResults, ...partialMatches];
        }
      }
      
      // For multi-word searches, do additional client-side filtering
      if (words.length > 1) {
        allResults = allResults.filter(item => {
          // Check if all remaining words are contained in any keywords
          return words.slice(1).every(word => {
            if (!item.keywords) return false;
            
            // Check if any keyword contains this word
            return item.keywords.some(keyword => keyword.includes(word));
          });
        });
      }
      
      // Filter results by the current category
      allResults = allResults.filter(item => 
        item.inventoryType === getCategoryDisplayName(currentCategory)
      );
      
      // Sort by match quality first, then by relevance and date
      allResults.sort((a, b) => {
        // First by match quality (3 is best, 0 is worst)
        if (b.matchQuality !== a.matchQuality) {
          return b.matchQuality - a.matchQuality;
        }
        
        // Then check if name/brand contains the exact search term
        const aExactMatch = (a.name?.toLowerCase().includes(searchTermLower) || 
                            a.brand?.toLowerCase().includes(searchTermLower)) ? 1 : 0;
        const bExactMatch = (b.name?.toLowerCase().includes(searchTermLower) || 
                            b.brand?.toLowerCase().includes(searchTermLower)) ? 1 : 0;
        
        // Finally by updated date
        return bExactMatch - aExactMatch || 
               new Date(b.updatedAt) - new Date(a.updatedAt);
      });
      
      // Process results to remove internal properties
      const processedResults = allResults.map(({matchQuality, ...item}) => item);
      
      // Update the cache for this category and search term
      setSearchCache(prev => ({
        ...prev,
        [currentCategory]: {
          ...prev[currentCategory],
          [searchTermLower]: processedResults
        }
      }));
      
      // Store full results for pagination
      setFullSearchResults(processedResults);
      
      // Set initial page of results
      setCurrentResultsPage(1);
      setSearchResults(processedResults.slice(0, ITEMS_PER_PAGE));
      setHasMoreSearchResults(processedResults.length > ITEMS_PER_PAGE);
      
    } catch (error) {
      console.error('Error searching inventory:', error);
      setSearchResults([]);
      setFullSearchResults([]);
      setHasMoreSearchResults(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Load more search results
  const loadMoreSearchResults = () => {
    const nextPage = currentResultsPage + 1;
    const startIdx = currentResultsPage * ITEMS_PER_PAGE;
    const endIdx = startIdx + ITEMS_PER_PAGE;
    
    const nextPageResults = fullSearchResults.slice(startIdx, endIdx);
    
    if (nextPageResults.length > 0) {
      setSearchResults(prev => [...prev, ...nextPageResults]);
      setCurrentResultsPage(nextPage);
      setHasMoreSearchResults(endIdx < fullSearchResults.length);
    } else {
      setHasMoreSearchResults(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (index) => {
    setActiveTab(index);
    
    // Reset search when changing tabs
    if (searchTerm.trim() !== '') {
      setSearchTerm('');
      setIsSearching(false);
      setSearchResults([]);
    }
    
    // Load data for the selected tab if not already loaded
    const category = equipmentCategories[index].id;
    if (inventoryGroups[category].length === 0 && hasMoreItems[category]) {
      fetchInventoryByCategory(category);
    }
  };
  
  // Handle showing more items
  const handleShowMore = (category) => {
    if (isSearching) {
      loadMoreSearchResults();
    } else {
      fetchInventoryByCategory(category, true);
    }
  };
  
  // Get filtered inventory
  const getFilteredInventory = (category) => {
    if (isSearching) {
      // Filter search results for the current category
      return searchResults.filter(item => 
        item.inventoryType === getCategoryDisplayName(category)
      );
    }
    
    // Return regular inventory from the selected category
    return inventoryGroups[category] || [];
  };

  // Handle item editing
  const handleEditItem = (item, category) => {
    // Check if staff has permission to edit
    if (!isActionAllowed('edit')) {
      alert('You do not have permission to edit inventory items.');
      return;
    }
    
    setEditItem({ ...item, category });
    
    switch (category) {
      case 'audioEquipment':
        setShowAddAudioModal(true);
        break;
      case 'backlineEquipment':
        setShowAddBacklineModal(true);
        break;
      case 'lightingEquipment':
        setShowAddLightingModal(true);
        break;
      case 'visualEquipment':
        setShowAddVisualModal(true);
        break;
      case 'pyroEquipment':
        setShowAddPyroModal(true);
        break;
      case 'stagingEquipment':
        setShowAddStagingModal(true);
        break;
      case 'miscellaneousEquipment':
        setShowAddMiscellaneousModal(true);
        break;
    }
  };

  // Handle item detail view
  const handleViewDetails = (item, category) => {
    // Navigation to the item details page
    navigate(`/inventory/${category}/${item.id}`);
  };

  // Handle item deletion confirmation
  const handleDeleteConfirm = (item, category) => {
    // Check if staff has permission to delete
    if (!isActionAllowed('delete')) {
      alert('You do not have permission to delete inventory items.');
      return;
    }
    
    setItemToDelete({ ...item, category });
    setShowDeleteConfirm(true);
  };
  
  // Handle file viewing
  const handleViewFile = (item) => {
    if (item.fileUrl) {
      setFileViewerUrl(item.fileUrl);
      setFileViewerName(item.name || getFileName(item.fileUrl));
      
      // Detect file type from URL extension
      const fileUrl = item.fileUrl.toLowerCase();
      if (fileUrl.endsWith('.pdf')) {
        setFileViewerType('application/pdf');
      } else if (fileUrl.match(/\.(jpe?g|png|gif|bmp|webp)$/)) {
        // Use the specific image type rather than generic 'image'
        const extension = fileUrl.split('.').pop();
        setFileViewerType(`image/${extension === 'jpg' ? 'jpeg' : extension}`);
      } else if (fileUrl.match(/\.(docx?)$/)) {
        setFileViewerType('application/vnd.openxmlformats-officedocument.wordprocessingml.document');
      } else if (fileUrl.match(/\.(xlsx?)$/)) {
        setFileViewerType('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
      } else {
        setFileViewerType('application/octet-stream');
      }
      
      setShowFileViewer(true);
    }
  };
  
  // Handle opening assign inventory modal
  const handleOpenAssignInventory = () => {
    setShowAssignInventoryModal(true);
  };
  
  // Handle inventory assignment
  const handleInventoryAssigned = (assignmentData) => {
    // Refresh current tab data after assignment
    const currentCategory = equipmentCategories[activeTab].id;
    fetchInventoryByCategory(currentCategory);
  };
  
  // Extract filename from fileUrl if present
  const getFileName = (url) => {
    if (!url) return 'Document';
    
    try {
      const parts = url.split('/');
      // Get the last part of the URL (filename)
      const rawFilename = parts[parts.length - 1];
      // Decode any URL encoded characters
      return decodeURIComponent(rawFilename);
    } catch (error) {
      console.error('Error extracting filename:', error);
      return 'Document';
    }
  };

  // Handle actual item deletion
  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
  
    try {
      const { id, category } = itemToDelete;
      const db = getFirestoreInstance();
      
      // Store item data before deletion for notification
      const itemData = {
        id: itemToDelete.id,
        name: itemToDelete.name,
        type: getCategoryDisplayName(category),
        category: category
      };
      
      // Delete from the new structure
      const groupId = itemToDelete.groupId || id;
      
      // Delete the group document
      const groupRef = doc(db, 'users', userData.userId, 'inventoryGroups', groupId);
      await deleteDoc(groupRef);
      
      // Delete all associated individual items
      const itemsQuery = query(
        collection(db, 'users', userData.userId, 'inventoryItems'),
        where('groupId', '==', groupId)
      );
      
      const itemsSnap = await getDocs(itemsQuery);
      
      // Use a batch for efficient writes
      const batch = writeBatch(db);
      
      itemsSnap.forEach(docSnapshot => {
        batch.delete(docSnapshot.ref);
      });
      
      await batch.commit();
      
      // Update state
      setInventoryGroups(prev => ({
        ...prev,
        [category]: prev[category].filter(item => 
          item.id !== id && item.groupId !== groupId
        )
      }));
      
      // *** DELETE FILES FROM BACKBLAZE ***
      // Delete cover photo and file attachment from Backblaze
      if (itemToDelete.coverPhotoUrl) {
        try {
          // Extract the file path from the URL
          const coverPhotoPath = extractFilePathFromUrl(itemToDelete.coverPhotoUrl);
          if (coverPhotoPath) {
            await deleteFileFromBackblaze(coverPhotoPath);
            console.log('Cover photo deleted from Backblaze');
          }
        } catch (deleteError) {
          console.error('Error deleting cover photo from Backblaze:', deleteError);
        }
      }
      
      if (itemToDelete.fileUrl) {
        try {
          // Extract the file path from the URL
          const filePath = extractFilePathFromUrl(itemToDelete.fileUrl);
          if (filePath) {
            await deleteFileFromBackblaze(filePath);
            console.log('File attachment deleted from Backblaze');
          }
        } catch (deleteError) {
          console.error('Error deleting file attachment from Backblaze:', deleteError);
        }
      }
      
      // Also delete individual item photos if any
      for (const doc of itemsSnap.docs) {
        const item = doc.data();
        if (item.photoUrl) {
          try {
            const photoPath = extractFilePathFromUrl(item.photoUrl);
            if (photoPath) {
              await deleteFileFromBackblaze(photoPath);
              console.log(`Individual item photo deleted: ${item.serialNumber}`);
            }
          } catch (deleteError) {
            console.error(`Error deleting individual item photo for ${item.serialNumber}:`, deleteError);
          }
        }
      }
      
      // Clear the search cache for this category only
      setSearchCache(prev => ({
        ...prev,
        [category]: {} // Clear just this category's cache
      }));
      
      // If currently searching, refresh the search results to reflect the deletion
      if (isSearching && searchTerm.trim() !== '') {
        // Remove the deleted item from current search results
        setSearchResults(prev => 
          prev.filter(item => 
            item.id !== id && item.groupId !== groupId
          )
        );
        
        // Also update the full results for pagination
        setFullSearchResults(prev => 
          prev.filter(item => 
            item.id !== id && item.groupId !== groupId
          )
        );
      }
      
      // Send notification if user is staff
      if (userData && userData.userType === 'Staff' && userData.currentOrganizationId) {
        try {
          await sendInventoryUpdateNotification(
            userData.currentOrganizationId,
            {
              userId: userData.userId,
              fullName: userData.fullName || 'Staff member',
              userName: userData.userName,
              profilePictureUrl: userData.profilePictureUrl
            },
            'deleted',
            itemData
          );
        } catch (error) {
          console.error('Error sending notification:', error);
        }
      }
  
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };
  
  // Helper function to extract file path from Backblaze URL
  const extractFilePathFromUrl = (url) => {
    if (!url) return null;
    
    try {
      // Example URL format: https://Ontour-Media.s3.eu-central-003.backblazeb2.com/inventory/user123/audioEquipment/item456/photo.jpg
      const regex = /https:\/\/[^\/]+\.s3\.[^\/]+\.backblazeb2\.com\/(.+)/;
      const match = url.match(regex);
      
      if (match && match[1]) {
        return decodeURIComponent(match[1]);
      }
      return null;
    } catch (error) {
      console.error('Error extracting file path from URL:', error);
      return null;
    }
  };
  
  // Function to delete a file from Backblaze
  const deleteFileFromBackblaze = async (filePath) => {
    try {
      // Get auth token from localStorage
      const authToken = localStorage.getItem('authToken');
      
      // Send delete request to your backend API
      const response = await fetch('/api/deleteBackblazeFile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ filePath })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete file');
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting file from Backblaze:', error);
      throw error;
    }
  };


  // Modal close handlers
  const handleModalClose = () => {
    setEditItem(null);
    setShowAddAudioModal(false);
    setShowAddBacklineModal(false);
    setShowAddLightingModal(false);
    setShowAddVisualModal(false);
    setShowAddPyroModal(false);
    setShowAddStagingModal(false);
    setShowAddMiscellaneousModal(false);
    setShowAssignInventoryModal(false);
    
    // Clear edit parameter from URL
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('edit')) {
      searchParams.delete('edit');
      navigate({
        pathname: location.pathname,
        search: searchParams.toString()
      });
    }
  };

  // Item added handler
  const handleItemAdded = async (item, category) => {
    // Add to inventoryGroups
    setInventoryGroups(prev => {
      return {
        ...prev,
        [category]: [item, ...prev[category]]
      };
    });
    
    // Clear cache for this category only
    setSearchCache(prev => ({
      ...prev,
      [category]: {} // Clear just this category's cache
    }));
    
    // Send notification if user is staff
    if (userData && userData.userType === 'Staff' && userData.currentOrganizationId) {
      try {
        await sendInventoryUpdateNotification(
          userData.currentOrganizationId,
          {
            userId: userData.userId,
            fullName: userData.fullName || 'Staff member',
            userName: userData.userName,
            profilePictureUrl: userData.profilePictureUrl
          },
          'added',
          {
            id: item.id,
            name: item.name,
            type: getCategoryDisplayName(category),
            category: category
          }
        );
      } catch (error) {
        console.error('Error sending notification:', error);
      }
    }
    
    handleModalClose();
  };

  // Item updated handler
  const handleItemUpdated = async (updatedItem, category) => {
    // Update local state first (your existing code)
    setInventoryGroups(prev => ({
      ...prev,
      [category]: prev[category].map(item => 
        (item.id === updatedItem.id || item.groupId === updatedItem.groupId) ? updatedItem : item
      )
    }));
    
    // Clear cache for this category (your existing code)
    setSearchCache(prev => ({
      ...prev,
      [category]: {}
    }));
    
    // NEW CODE - Update assigned inventory in tour dates
    try {
      // Check if the item has assigned tour dates
      if (updatedItem.assignedTourIds && Object.keys(updatedItem.assignedTourIds).length > 0) {
        const db = getFirestoreInstance();
        
        // For each assigned tour
        for (const [tourId, tourDetails] of Object.entries(updatedItem.assignedTourIds)) {
          if (!tourDetails.assignedDates || tourDetails.assignedDates.length === 0) {
            continue;
          }
          
          // For each assigned date
          for (const dateInfo of tourDetails.assignedDates) {
            if (!dateInfo.tourDateDocId) continue;
            
            // Instead of updating fields directly, update the subcollections
            
            // 1. Update individual items in assignedInventoryItems subcollection
            const itemsQuery = query(
              collection(db, 'users', userData.userId, 'inventoryItems'),
              where('groupId', '==', updatedItem.id)
            );
            
            const itemsSnapshot = await getDocs(itemsQuery);
            
            // For each individual item in this group
            for (const itemDoc of itemsSnapshot.docs) {
              const itemId = itemDoc.id;
              
              // Create reference to the item in the subcollection
              const assignedItemRef = doc(
                db, 
                'Tours', 
                tourId, 
                'tourDates', 
                dateInfo.tourDateDocId, 
                'assignedInventoryItems', 
                itemId
              );
              
              // Check if this item exists in the subcollection
              const assignedItemDoc = await getDoc(assignedItemRef);
              
              if (assignedItemDoc.exists()) {
                // Update the existing document with new information
                await updateDoc(assignedItemRef, {
                  name: updatedItem.name,
                  brand: updatedItem.brand,
                  model: updatedItem.model || '',
                  subType: updatedItem.subType || '',
                  type: updatedItem.type || '',
                  rentPerDay: updatedItem.rentPerDay,
                  updatedAt: new Date().toISOString()
                });
              }
            }
            
            // 2. Update group information in assignedInventoryGroups subcollection
            const assignedGroupRef = doc(
              db, 
              'Tours', 
              tourId, 
              'tourDates', 
              dateInfo.tourDateDocId, 
              'assignedInventoryGroups', 
              updatedItem.id
            );
            
            // Check if the group exists in the subcollection
            const assignedGroupDoc = await getDoc(assignedGroupRef);
            
            if (assignedGroupDoc.exists()) {
              // Update the existing document with new information
              await updateDoc(assignedGroupRef, {
                brand: updatedItem.brand,
                coverPhotoUrl: updatedItem.coverPhotoUrl || null,
                description: updatedItem.description || '',
                fileUrl: updatedItem.fileUrl || null,
                model: updatedItem.model || '',
                name: updatedItem.name,
                rentPerDay: updatedItem.rentPerDay,
                subType: updatedItem.subType || '',
                type: updatedItem.type || '',
                updatedAt: new Date().toISOString()
              });
            }
            
            // 3. Remove old fields if they exist (migration step)
            const tourDateRef = doc(db, 'Tours', tourId, 'tourDates', dateInfo.tourDateDocId);
            const tourDateDoc = await getDoc(tourDateRef);
            
            if (tourDateDoc.exists()) {
              const tourDateData = tourDateDoc.data();
              
              // If old fields exist, remove them
              if (tourDateData.assignedInventoryItems || tourDateData.assignedInventoryGroups) {
                await updateDoc(tourDateRef, {
                  inventoryStoredAsSubcollections: true,
                  assignedInventoryItems: deleteField(),
                  assignedInventoryGroups: deleteField()
                });
              }
            }
          }
        }
      }
      
      // Send notification (your existing code)
      if (userData && userData.userType === 'Staff' && userData.currentOrganizationId) {
        try {
          await sendInventoryUpdateNotification(
            userData.currentOrganizationId,
            {
              userId: userData.userId,
              fullName: userData.fullName || 'Staff member',
              userName: userData.userName,
              profilePictureUrl: userData.profilePictureUrl
            },
            'edited',
            {
              id: updatedItem.id,
              name: updatedItem.name,
              type: getCategoryDisplayName(category),
              category: category
            }
          );
        } catch (error) {
          console.error('Error sending notification:', error);
        }
      }
      
      handleModalClose();
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item. Please try again.');
    }
  };

  // Handle tour selection (for RightMenu)
  const handleTourSelect = (tour) => {
    // Navigate to tour or update state as needed
  };
  
  // Render the equipment card
  const renderEquipmentCard = (item, category) => {
    return (
      <div className={styles.equipmentCard} key={item.id}>
        <div className={styles.cardImageContainer} onClick={() => handleViewDetails(item, category)}>
          {item.coverPhotoUrl ? (
            <img 
              src={item.coverPhotoUrl} 
              alt={item.name} 
              className={styles.cardImage}
            />
          ) : (
            <div className={styles.defaultImage}>
              <span>{item.name.charAt(0)}</span>
            </div>
          )}
        </div>
        
        <div className={styles.cardContent}>
          <h3 className={styles.cardTitle} onClick={() => handleViewDetails(item, category)}>
            {item.name}
          </h3>
          
          <p className={styles.cardBrand}>
            {item.brand} {item.model && `- ${item.model}`}
            {!item.model && item.subType && ` - ${item.subType}`}
          </p>
          
          {item.description && (
            <p className={styles.cardDescription}>{item.description}</p>
          )}
          
          <div className={styles.cardDetails}>
            <span>
              Qty: {item.totalQuantity} 
              {item.availableQuantity < item.totalQuantity && 
              ` (${item.availableQuantity} available)`}
            </span>
            <span>₹{item.rentPerDay}/day</span>
          </div>
          
          {/* Status indicators - ALWAYS show all status counts */}
          <div className={styles.statusIndicators}>
            <span className={`${styles.statusBadge} ${styles.availableBadge}`}>
              {item.availableQuantity || 0} Available
            </span>
            <span className={`${styles.statusBadge} ${styles.reservedBadge}`}>
              {item.reservedQuantity || 0} Reserved
            </span>
            <span className={`${styles.statusBadge} ${styles.maintenanceBadge}`}>
              {item.maintenanceQuantity || 0} Maintenance
            </span>
            <span className={`${styles.statusBadge} ${styles.damagedBadge}`}>
              {item.damagedQuantity || 0} Damaged
            </span>
          </div>
          
          {/* File indicator */}
          {item.fileUrl && (
            <div className={styles.fileIndicator} onClick={() => handleViewFile(item)}>
              <FontAwesomeIcon 
                icon={item.fileUrl.toLowerCase().endsWith('.pdf') 
                  ? faFilePdf : faFileImage} 
              />
              <span>View Attachment</span>
            </div>
          )}
          
          <div className={styles.cardActions}>
            <button 
              className={styles.actionButton} 
              onClick={() => handleViewDetails(item, category)}
              aria-label="View item details"
            >
              <FontAwesomeIcon icon={faEye} />
            </button>
            
            {/* Only show edit button if allowed */}
            {isActionAllowed('edit') && (
              <button 
                className={styles.actionButton} 
                onClick={() => handleEditItem(item, category)}
                aria-label="Edit item"
              >
                <FontAwesomeIcon icon={faEdit} />
              </button>
            )}
            
            {/* Only show delete button if allowed */}
            {isActionAllowed('delete') && (
              <button 
                className={styles.actionButton} 
                onClick={() => handleDeleteConfirm(item, category)}
                aria-label="Delete item"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  // Render equipment section for each category
  const renderEquipmentSection = (category) => {
    const filteredItems = getFilteredInventory(category);
    
    return (
      <div className={styles.equipmentSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.searchContainer}>
            <div className={styles.searchBar}>
              <input 
                type="text" 
                placeholder="Search inventory..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                className={styles.searchInput}
              />
              <button 
                className={styles.searchButton}
                onClick={handleSearch}
                disabled={isLoading}
              >
                {isLoading ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faSearch} />
                )}
              </button>
            </div>
          </div>
          <div className={styles.actionButtons}>
            <button 
              className={styles.assignButton}
              onClick={handleOpenAssignInventory}
            >
              <FontAwesomeIcon icon={faExchangeAlt} />
              <span>Assign Inventory</span>
            </button>
            
            {/* Only show add button if allowed */}
            {isActionAllowed('add') && (
              <button 
                className={styles.addButton}
                onClick={() => handleAddItem(category)}
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Add Equipment</span>
              </button>
            )}
          </div>
        </div>
        
        {/* Rest of the render function */}
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <FontAwesomeIcon icon={faSpinner} spin className={styles.spinner} />
            <p>Loading inventory items...</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className={styles.emptyState}>
            <FontAwesomeIcon icon={faFileAlt} className={styles.emptyIcon} />
            {isSearching ? (
              <p>No items matching your search in this category.</p>
            ) : (
              <>
                <p>No items in this category yet.</p>
                {/* Only show add button in empty state if allowed */}
                {isActionAllowed('add') && (
                  <button 
                    className={styles.addEmptyButton}
                    onClick={() => handleAddItem(category)}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>Add {category.replace('Equipment', '')} Equipment</span>
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className={styles.cardGrid}>
              {filteredItems.map(item => renderEquipmentCard(item, category))}
            </div>
            
            {isSearching ? 
              (hasMoreSearchResults && (
                <div className={styles.showMoreContainer}>
                  <button 
                    className={styles.showMoreButton}
                    onClick={() => loadMoreSearchResults()}
                  >
                    Show More Results
                  </button>
                </div>
              )) : 
              (hasMoreItems[category] && (
                <div className={styles.showMoreContainer}>
                  <button 
                    className={styles.showMoreButton}
                    onClick={() => handleShowMore(category)}
                  >
                    Show More
                  </button>
                </div>
              ))
            }
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.inventoryContainer}>
      <div className={styles['main-layout']}>
        {!isMobile && <LeftMenu />}
        <main className={styles['inventory-main']}>
        <AuthCheckWrapper>
        {hasAccess ? (
            <>
          <div className={styles.pageHeader}>
            <h1 className={styles.pageTitle}>Inventory Management</h1>
            <p className={styles.pageDescription}>
              Manage your equipment inventory across multiple categories.
            </p>
          </div>

          <div className={styles.tabContent}>
            <div className={styles.tabList}>
              {equipmentCategories.map((category, index) => (
                <button
                  key={category.id}
                  className={`${styles.tab} ${activeTab === index ? styles.activeTab : ''}`}
                  onClick={() => handleTabChange(index)}
                >
                  {category.label}
                </button>
              ))}
            </div>

            <div className={styles.tabContent}>
              {equipmentCategories.map((category, index) => (
                <div
                  key={category.id}
                  className={`${styles.tabPanel} ${activeTab === index ? styles.activePanel : ''}`}
                >
                  {activeTab === index && renderEquipmentSection(category.id)}
                </div>
              ))}
            </div>
          </div>

          {/* Add Equipment Modals */}
          <AddAudioEquipmentModal
            isOpen={showAddAudioModal}
            onClose={handleModalClose}
            onSave={editItem ? handleItemUpdated : handleItemAdded}
            editItem={editItem && editItem.category === 'audioEquipment' ? editItem : null}
          />
          
          <AddBacklineEquipmentModal
            isOpen={showAddBacklineModal}
            onClose={handleModalClose}
            onSave={editItem ? handleItemUpdated : handleItemAdded}
            editItem={editItem && editItem.category === 'backlineEquipment' ? editItem : null}
          />
          
          <AddLightingEquipmentModal
            isOpen={showAddLightingModal}
            onClose={handleModalClose}
            onSave={editItem ? handleItemUpdated : handleItemAdded}
            editItem={editItem && editItem.category === 'lightingEquipment' ? editItem : null}
          />
          
          <AddVisualEquipmentModal
            isOpen={showAddVisualModal}
            onClose={handleModalClose}
            onSave={editItem ? handleItemUpdated : handleItemAdded}
            editItem={editItem && editItem.category === 'visualEquipment' ? editItem : null}
          />
          
          <AddPyroEquipmentModal
            isOpen={showAddPyroModal}
            onClose={handleModalClose}
            onSave={editItem ? handleItemUpdated : handleItemAdded}
            editItem={editItem && editItem.category === 'pyroEquipment' ? editItem : null}
          />
          
          <AddStagingEquipmentModal
            isOpen={showAddStagingModal}
            onClose={handleModalClose}
            onSave={editItem ? handleItemUpdated : handleItemAdded}
            editItem={editItem && editItem.category === 'stagingEquipment' ? editItem : null}
          />
          
          <AddMiscellaneousEquipmentModal
            isOpen={showAddMiscellaneousModal}
            onClose={handleModalClose}
            onSave={editItem ? handleItemUpdated : handleItemAdded}
            editItem={editItem && editItem.category === 'miscellaneousEquipment' ? editItem : null}
          />
          
          {/* Assign Inventory Modal */}
          <AssignInventoryModal
            isOpen={showAssignInventoryModal}
            onClose={handleModalClose}
            onAssign={handleInventoryAssigned}
          />

          {/* Delete Confirmation Modal */}
          <ConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDeleteItem}
            title="Delete Item"
            message="Are you sure you want to delete this equipment? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            confirmButtonClass="dangerButton"
          />
          
          {/* File Viewer Modal */}
          {showFileViewer && (
            <FileViewerModal
              isOpen={showFileViewer}
              onClose={() => setShowFileViewer(false)}
              file={{
                fileUrl: fileViewerUrl,
                fileType: fileViewerType,
                fileName: fileViewerName || 'Document'
              }}
            />
          )}
          </>
          ) : (
            <PermissionDenied />
          )}
          </AuthCheckWrapper>
        </main>
        {!isMobile && <RightMenu tours={tours} onTourSelect={handleTourSelect} />}
      </div>
    </div>
  );
};

export default Inventory;